import React, { useEffect, useState, useCallback } from "react";
import { apiClient, formatSAR } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import {
  Package, Plus, Trash2, Star, Sparkles, TrendingUp, Users, Layers,
  Edit3, X, Save, Loader2, Info, Lock,
} from "lucide-react";

const empty = { name: "", name_ar: "", price_sar: 0, duration_days: 30, features: [], color: "#c9a55c", active: true };

/** Live Super-Admin stats card for the Yanbu Founding Showrooms offer.
    Numbers auto-refresh whenever the tab is opened. Read-only. */
function YanbuOfferStatsCard() {
  const { lang } = useI18n();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    apiClient.get("/platform/plans/admin/yanbu-offer/stats")
      .then(({ data }) => { if (!dead) setStats(data); })
      .catch(() => { if (!dead) setStats({ error: true }); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  if (loading) return null;
  if (!stats || stats.error) return null;

  const cards = [
    { icon: Users, label: lang === "ar" ? "المعارض المؤهلة" : "Eligible showrooms", value: stats.eligible_count, testid: "yanbu-stat-eligible" },
    { icon: Sparkles, label: lang === "ar" ? "المعارض التي فعّلت العرض" : "Activated the offer", value: stats.activated_count, testid: "yanbu-stat-activated" },
    { icon: TrendingUp, label: lang === "ar" ? "نسبة التفعيل" : "Activation rate", value: `${stats.activation_rate}%`, testid: "yanbu-stat-rate" },
  ];

  return (
    <div className="surface rounded-2xl p-6 space-y-4" data-testid="yanbu-offer-stats-card"
         style={{ background: "linear-gradient(135deg,#fbf6ec,#fff)", borderColor: "#d4a24a" }}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#a8893e", color: "#fff" }}>
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">
            {lang === "ar" ? "قياس الاستفادة من العرض" : "Offer performance"}
          </div>
          <div className="font-display text-xl text-[#0a0a0a]">
            {lang === "ar" ? stats.name_ar : stats.name_en}
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {cards.map((c) => (
          <div key={c.testid} className="rounded-xl border p-4 bg-white" style={{ borderColor: "#e6d9b8" }} data-testid={c.testid}>
            <div className="flex items-center gap-2 text-xs text-[#8a6f2e]">
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </div>
            <div className="font-display text-3xl mt-2 text-[#0a0a0a]">{c.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// iter274 — Unified packages view. Source-of-truth aggregation, NO
// fabrication. Reads from THREE existing collections without altering
// any of them:
//   1. platform_plans                (subscription plans — includes
//      the `enterprise` / المؤسسات plan that was previously invisible
//      here despite being referenced by Private Offers).
//   2. platform_promotion_packages   (listing upsells — read-only in
//      this UI because no admin-PATCH route exists yet; surfaced
//      honestly with a lock badge instead of inventing edit power).
//   3. featured_packages             (the original "باقات التمييز"
//      the tab already managed; behaviour preserved exactly).
// ────────────────────────────────────────────────────────────────────
export default function PackagesTab() {
  const { lang } = useI18n();

  // ── Featured packages (original, unchanged) ──────────────────────
  const [featured, setFeatured] = useState([]);
  const [form, setForm] = useState(empty);
  const [featuresText, setFeaturesText] = useState("");

  // ── Subscription plans (platform_plans) — NEW ────────────────────
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null); // full plan object being edited

  // ── Promotion packages (read-only) — NEW ─────────────────────────
  const [promos, setPromos] = useState([]);
  const [promosLoading, setPromosLoading] = useState(true);

  const loadFeatured = useCallback(() =>
    apiClient.get("/admin/packages").then(({ data }) => setFeatured(data.items || [])), []);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const { data } = await apiClient.get("/platform/plans/admin/plans");
      // Sort by sort_order then key so the order in the DB is honoured.
      const sorted = (data.items || []).slice().sort((a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.key).localeCompare(b.key));
      setPlans(sorted);
    } finally { setPlansLoading(false); }
  }, []);

  const loadPromos = useCallback(async () => {
    setPromosLoading(true);
    try {
      const { data } = await apiClient.get("/platform/plans/promotions");
      setPromos(data.items || []);
    } finally { setPromosLoading(false); }
  }, []);

  useEffect(() => { loadFeatured(); loadPlans(); loadPromos(); }, [loadFeatured, loadPlans, loadPromos]);

  // ── Featured (unchanged behaviour) ───────────────────────────────
  const submitFeatured = async (e) => {
    e.preventDefault();
    const payload = { ...form, features: featuresText.split("\n").map(s => s.trim()).filter(Boolean) };
    await apiClient.post("/admin/packages", payload);
    setForm(empty); setFeaturesText("");
    loadFeatured();
  };
  const toggleFeatured = async (p) => { await apiClient.patch(`/admin/packages/${p.id}`, { active: !p.active }); loadFeatured(); };
  const delFeatured = async (id) => {
    if (!window.confirm(lang === "ar" ? "حذف الباقة؟" : "Delete package?")) return;
    await apiClient.delete(`/admin/packages/${id}`); loadFeatured();
  };

  // ── Subscription plan edit ───────────────────────────────────────
  const savePlan = async (patch) => {
    // Never send the immutable `key` — the backend keys the URL off it.
    const { key, is_system, id, created_at, updated_at, ...body } = patch;
    await apiClient.patch(`/platform/plans/admin/plans/${key}`, body);
    setEditingPlan(null);
    loadPlans();
  };
  const togglePlan = async (plan, field) => {
    await apiClient.patch(`/platform/plans/admin/plans/${plan.key}`, { [field]: !plan[field] });
    loadPlans();
  };

  return (
    <div data-testid="admin-packages" className="space-y-8">
      <YanbuOfferStatsCard />

      {/* ══════ Section 1 — Subscription plans (source-of-truth) ══════ */}
      <section className="space-y-4" data-testid="section-subscription-plans">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#8a6f2e]" />
              {lang === "ar" ? "باقات الاشتراك" : "Subscription plans"}
            </h2>
            <p className="text-xs text-[#6b6b6b] mt-1 max-w-2xl">
              {lang === "ar"
                ? "المصدر الفعلي لجميع باقات الاشتراك في CARTAL (platform_plans). تُستخدم في العروض الخاصة والحملات وحسابات المؤسسات — تعديل السعر أو الوصف أو الحدود يُطبَّق فورًا."
                : "The real source-of-truth for CARTAL subscription plans (platform_plans). Used across private offers, campaigns, and institutional accounts."}
            </p>
          </div>
        </div>
        {plansLoading ? (
          <div className="surface rounded-xl p-8 text-center text-[#8a8a8a]">
            <Loader2 className="w-5 h-5 animate-spin inline" />
          </div>
        ) : plans.length === 0 ? (
          <div className="surface rounded-xl p-6 text-sm text-[#6b6b6b]">
            {lang === "ar" ? "لا توجد باقات." : "No plans."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
            {plans.map((p) => (
              <SubscriptionPlanCard
                key={p.key}
                plan={p}
                lang={lang}
                onEdit={() => setEditingPlan(p)}
                onToggleActive={() => togglePlan(p, "is_active")}
                onTogglePublic={() => togglePlan(p, "is_public")}
              />
            ))}
          </div>
        )}
      </section>

      {/* ══════ Section 2 — Promotion packages (listing upsells) ══════ */}
      <section className="space-y-4" data-testid="section-promotion-packages">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#8a6f2e]" />
              {lang === "ar" ? "باقات الترقية للإعلانات" : "Listing promotion packages"}
            </h2>
            <p className="text-xs text-[#6b6b6b] mt-1 max-w-2xl inline-flex items-center gap-1">
              <Info className="w-3.5 h-3.5" />
              {lang === "ar"
                ? "من المجموعة الحالية platform_promotion_packages — للعرض فقط، بدون تعديل مباشر (لم يتم إنشاء endpoint تعديل لتفادي المساس بمنطق الشراء)."
                : "Read-only view of platform_promotion_packages (no admin PATCH route exists yet)."}
            </p>
          </div>
        </div>
        {promosLoading ? (
          <div className="surface rounded-xl p-6 text-center text-[#8a8a8a]">
            <Loader2 className="w-5 h-5 animate-spin inline" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="promos-grid">
            {promos.map((p) => (
              <div key={p.key} className="surface rounded-xl p-4" data-testid={`promo-${p.key}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display text-base">{lang === "ar" ? p.name_ar : p.name_en}</div>
                    <div className="text-[10px] text-[#8a8a8a] font-mono">{p.key}</div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#8a6f2e] shrink-0"
                        title={lang === "ar" ? "للقراءة فقط" : "Read-only"}>
                    <Lock className="w-3 h-3" /> {lang === "ar" ? "قراءة" : "RO"}
                  </span>
                </div>
                <div className="font-display text-2xl my-2 text-[#0a0a0a]">
                  {formatSAR(p.price_sar || 0)} <span className="text-xs text-[#8a8a8a]">SAR</span>
                </div>
                <div className="text-[11px] text-[#6b6b6b]">
                  {p.duration_days} {lang === "ar" ? "يوم" : "days"} ·{" "}
                  {p.is_active
                    ? <span className="text-emerald-700">{lang === "ar" ? "نشطة" : "Active"}</span>
                    : <span className="text-amber-700">{lang === "ar" ? "معطّلة" : "Inactive"}</span>}
                </div>
                {p.description_ar && lang === "ar" && (
                  <p className="text-[11px] text-[#3a3a3a] mt-2 leading-relaxed">{p.description_ar}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ══════ Section 3 — Featured packages (unchanged) ══════ */}
      <section className="space-y-4" data-testid="section-featured-packages">
        <h2 className="font-display text-2xl flex items-center gap-2">
          <Package className="w-5 h-5 text-[#8a6f2e]" />
          {lang === "ar" ? "باقات التمييز (Featured)" : "Featured packages"}
        </h2>
        <p className="text-xs text-[#6b6b6b] max-w-2xl -mt-2">
          {lang === "ar"
            ? "الباقات المخصصة الحرة التي أضفتها إدارة CARTAL (featured_packages). لا تتداخل مع باقات الاشتراك أعلاه."
            : "Free-form featured packages added by CARTAL admins (featured_packages)."}
        </p>

        <form onSubmit={submitFeatured} className="surface rounded-xl p-6 grid sm:grid-cols-2 gap-4">
          <input className="input-dark" placeholder={lang === "ar" ? "اسم (إنجليزي)" : "Name (EN)"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="package-name" />
          <input className="input-dark" placeholder={lang === "ar" ? "اسم (عربي)" : "Name (AR)"} value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="package-name-ar" />
          <input className="input-dark" type="number" placeholder={lang === "ar" ? "السعر بالريال" : "Price (SAR)"} value={form.price_sar} onChange={(e) => setForm({ ...form, price_sar: Number(e.target.value) })} required data-testid="package-price" />
          <input className="input-dark" type="number" placeholder={lang === "ar" ? "المدة (يوم)" : "Duration (days)"} value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })} required data-testid="package-duration" />
          <textarea className="input-dark sm:col-span-2 min-h-[80px]" placeholder={lang === "ar" ? "الميزات (سطر لكل ميزة)" : "Features (one per line)"} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} data-testid="package-features" />
          <button type="submit" className="btn-primary sm:col-span-2" data-testid="package-create"><Plus className="w-4 h-4" /> {lang === "ar" ? "إضافة باقة" : "Add package"}</button>
        </form>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {featured.map(p => (
            <div key={p.id} className="surface rounded-xl p-5" data-testid={`package-${p.id}`} style={{ borderTop: `3px solid ${p.color || "#c9a55c"}` }}>
              <div className="flex justify-between gap-2">
                <div>
                  <div className="font-display text-lg">{lang === "ar" ? (p.name_ar || p.name) : p.name}</div>
                  <div className="text-xs text-[#6b6b6b]">{p.duration_days} {lang === "ar" ? "يوم" : "days"}</div>
                </div>
                <span className={`pill text-[10px] ${p.active ? "pill-accent" : ""}`}>{p.active ? (lang === "ar" ? "نشطة" : "Active") : (lang === "ar" ? "معطّلة" : "Inactive")}</span>
              </div>
              <div className="font-display text-2xl my-3 text-[#0a0a0a]">{formatSAR(p.price_sar)} <span className="text-xs text-[#8a8a8a]">SAR</span></div>
              <ul className="text-xs text-[#3a3a3a] space-y-1 mb-3">
                {(p.features || []).map((f, i) => <li key={i} className="flex items-start gap-1.5"><Star className="w-3 h-3 text-[#c9a55c] mt-0.5 flex-shrink-0" /> <span>{f}</span></li>)}
              </ul>
              <div className="flex gap-2">
                <button onClick={() => toggleFeatured(p)} className="btn-ghost text-xs" data-testid={`package-toggle-${p.id}`}>{p.active ? (lang === "ar" ? "تعطيل" : "Disable") : (lang === "ar" ? "تفعيل" : "Enable")}</button>
                <button onClick={() => delFeatured(p.id)} className="btn-ghost text-xs text-red-700 border-red-300" data-testid={`package-delete-${p.id}`}><Trash2 className="w-3 h-3" /> {lang === "ar" ? "حذف" : "Delete"}</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Edit-plan modal */}
      {editingPlan && (
        <SubscriptionPlanEditModal
          plan={editingPlan}
          lang={lang}
          onClose={() => setEditingPlan(null)}
          onSave={savePlan}
        />
      )}
    </div>
  );
}

// ── Subscription plan card ──────────────────────────────────────────
function SubscriptionPlanCard({ plan, lang, onEdit, onToggleActive, onTogglePublic }) {
  const isPromo = !!plan.is_promo;
  const isSystem = !!plan.is_system;
  const price = plan.price_monthly_sar ?? 0;
  const priceYearly = plan.price_yearly_sar ?? 0;
  const limits = plan.limits || {};
  const features = plan.features || {};

  return (
    <div className="surface rounded-xl p-5" data-testid={`plan-${plan.key}`}
         style={{ borderTop: `3px solid ${isPromo ? "#a8893e" : "#c9a55c"}` }}>
      <div className="flex justify-between items-start gap-2">
        <div>
          <div className="font-display text-lg">{lang === "ar" ? plan.name_ar : plan.name_en}</div>
          <div className="text-[10px] text-[#8a8a8a] font-mono mt-0.5">{plan.key}</div>
        </div>
        <div className="flex flex-col gap-1 items-end shrink-0">
          <span className={`pill text-[10px] ${plan.is_active ? "pill-accent" : ""}`}>
            {plan.is_active ? (lang === "ar" ? "نشطة" : "Active") : (lang === "ar" ? "معطّلة" : "Inactive")}
          </span>
          {!plan.is_public && (
            <span className="text-[9px] text-[#8a6f2e] uppercase tracking-widest">
              {lang === "ar" ? "مخفية" : "Hidden"}
            </span>
          )}
          {isPromo && (
            <span className="text-[9px] text-[#a8893e] uppercase tracking-widest">
              {lang === "ar" ? "عرض ترويجي" : "Promo"}
            </span>
          )}
        </div>
      </div>

      <div className="my-3">
        <div className="font-display text-2xl text-[#0a0a0a]">
          {formatSAR(price)} <span className="text-xs text-[#8a8a8a]">SAR/{lang === "ar" ? "شهر" : "mo"}</span>
        </div>
        {priceYearly > 0 && (
          <div className="text-[11px] text-[#8a8a8a]">
            {formatSAR(priceYearly)} SAR/{lang === "ar" ? "سنة" : "yr"}
          </div>
        )}
      </div>

      {(plan.description_ar || plan.description_en) && (
        <p className="text-xs text-[#525252] leading-relaxed mb-3">
          {lang === "ar" ? plan.description_ar : plan.description_en}
        </p>
      )}

      {/* Limits — only surface non-default keys the plan actually defines */}
      <div className="text-[11px] text-[#3a3a3a] space-y-0.5 mb-2">
        <LimitRow k="max_listings" v={limits.max_listings} label={lang === "ar" ? "الإعلانات" : "Listings"} />
        <LimitRow k="max_featured_listings" v={limits.max_featured_listings} label={lang === "ar" ? "المميّزة" : "Featured"} />
        <LimitRow k="max_employees" v={limits.max_employees} label={lang === "ar" ? "الموظفون" : "Employees"} />
        <LimitRow k="max_branches" v={limits.max_branches} label={lang === "ar" ? "الفروع" : "Branches"} />
        <LimitRow k="storage_gb" v={limits.storage_gb} label={lang === "ar" ? "التخزين (GB)" : "Storage (GB)"} />
        <LimitRow k="advertising_credits" v={limits.advertising_credits} label={lang === "ar" ? "أرصدة الإعلانات" : "Ad credits"} />
      </div>

      {/* Feature flags */}
      <div className="flex flex-wrap gap-1 mb-3">
        {Object.entries(features).filter(([, v]) => v).map(([k]) => (
          <span key={k} className="text-[10px] px-1.5 py-0.5 rounded bg-[#f5efdf] text-[#8a6f2e]" data-testid={`plan-${plan.key}-feature-${k}`}>
            {featureLabel(k, lang)}
          </span>
        ))}
      </div>

      <div className="flex gap-1 flex-wrap">
        <button onClick={onEdit} className="btn-ghost text-xs" data-testid={`plan-edit-${plan.key}`}>
          <Edit3 className="w-3 h-3" /> {lang === "ar" ? "تعديل" : "Edit"}
        </button>
        <button onClick={onToggleActive} className="btn-ghost text-xs" data-testid={`plan-toggle-active-${plan.key}`}>
          {plan.is_active ? (lang === "ar" ? "تعطيل" : "Disable") : (lang === "ar" ? "تفعيل" : "Enable")}
        </button>
        <button onClick={onTogglePublic} className="btn-ghost text-xs" data-testid={`plan-toggle-public-${plan.key}`}>
          {plan.is_public ? (lang === "ar" ? "إخفاء" : "Hide") : (lang === "ar" ? "إظهار" : "Show")}
        </button>
        {isSystem && (
          <span className="text-[9px] text-[#8a8a8a] uppercase tracking-widest inline-flex items-center gap-0.5" title={lang === "ar" ? "باقة نظام (لا يمكن حذفها)" : "System plan"}>
            <Lock className="w-2.5 h-2.5" /> {lang === "ar" ? "نظام" : "system"}
          </span>
        )}
      </div>
    </div>
  );
}

function LimitRow({ k, v, label }) {
  if (v === undefined || v === null) return null;
  const disp = v === -1 ? "∞" : (v?.toLocaleString?.() ?? v);
  return (
    <div className="flex justify-between gap-2">
      <span className="text-[#8a8a8a]">{label}</span>
      <span className="font-mono" data-testid={`limit-${k}`}>{disp}</span>
    </div>
  );
}

function featureLabel(k, lang) {
  const map = {
    analytics:                       { ar: "التحليلات", en: "Analytics" },
    advanced_dashboard:              { ar: "لوحة متقدمة", en: "Adv. dashboard" },
    business_profile_customization:  { ar: "تخصيص الملف", en: "Profile custom" },
    priority_support:                { ar: "دعم أولوية", en: "Priority support" },
    api_access:                      { ar: "الوصول للـAPI", en: "API access" },
  };
  return map[k]?.[lang] || k;
}

// ── Subscription-plan edit modal ────────────────────────────────────
// Small, opinionated editor: only the fields the backend PATCH route
// (`admin_update_plan`) accepts. Nothing else is written back to avoid
// accidental schema drift.
function SubscriptionPlanEditModal({ plan, lang, onClose, onSave }) {
  const [form, setForm] = useState(() => ({
    ...plan,
    limits: { ...(plan.limits || {}) },
    features: { ...(plan.features || {}) },
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const setLimit = (k, v) => setForm((f) => ({ ...f, limits: { ...f.limits, [k]: v === "" ? null : Number(v) } }));
  const setFeat  = (k, v) => setForm((f) => ({ ...f, features: { ...f.features, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { await onSave(form); }
    catch (ex) { setErr(ex.response?.data?.detail || (lang === "ar" ? "فشل الحفظ" : "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-[#0a0a0a]/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#f1ecdf] rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="plan-edit-modal">
        <div className="flex items-center justify-between p-5 border-b border-[#0a0a0a]/[0.08] sticky top-0 bg-[#f1ecdf] z-10">
          <div>
            <h3 className="font-display text-xl">
              {lang === "ar" ? "تعديل باقة الاشتراك" : "Edit subscription plan"}
            </h3>
            <div className="text-[10px] font-mono text-[#8a8a8a] mt-1">
              {plan.key} {plan.is_system && (lang === "ar" ? "· باقة نظام (المعرّف ثابت)" : "· system plan (immutable key)")}
            </div>
          </div>
          <button onClick={onClose} className="text-[#6b6b6b] hover:text-[#0a0a0a]" data-testid="plan-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {err && <div className="text-sm text-red-700 surface rounded-lg p-3">{String(err)}</div>}

          <fieldset className="grid sm:grid-cols-2 gap-3">
            <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">
              {lang === "ar" ? "الأسماء والوصف" : "Names & description"}
            </legend>
            <input className="input-dark" placeholder="Name AR" value={form.name_ar || ""} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} data-testid="plan-name-ar" />
            <input className="input-dark" placeholder="Name EN" value={form.name_en || ""} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="plan-name-en" />
            <textarea className="input-dark sm:col-span-2" placeholder="Description AR" value={form.description_ar || ""} onChange={(e) => setForm({ ...form, description_ar: e.target.value })} />
            <textarea className="input-dark sm:col-span-2" placeholder="Description EN" value={form.description_en || ""} onChange={(e) => setForm({ ...form, description_en: e.target.value })} />
          </fieldset>

          <fieldset className="grid sm:grid-cols-2 gap-3">
            <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">
              {lang === "ar" ? "التسعير" : "Pricing"}
            </legend>
            <label className="text-xs">
              <span className="block text-[#525252] mb-1">{lang === "ar" ? "شهريًا (SAR)" : "Monthly (SAR)"}</span>
              <input className="input-dark w-full" type="number" step="0.01" value={form.price_monthly_sar ?? 0} onChange={(e) => setForm({ ...form, price_monthly_sar: Number(e.target.value) })} data-testid="plan-price-monthly" />
            </label>
            <label className="text-xs">
              <span className="block text-[#525252] mb-1">{lang === "ar" ? "سنويًا (SAR)" : "Yearly (SAR)"}</span>
              <input className="input-dark w-full" type="number" step="0.01" value={form.price_yearly_sar ?? 0} onChange={(e) => setForm({ ...form, price_yearly_sar: Number(e.target.value) })} data-testid="plan-price-yearly" />
            </label>
          </fieldset>

          <fieldset className="grid sm:grid-cols-2 gap-3">
            <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">
              {lang === "ar" ? "الحدود (‑1 = بلا حد)" : "Limits (‑1 = unlimited)"}
            </legend>
            {[
              ["max_listings", lang === "ar" ? "الإعلانات" : "Listings"],
              ["max_featured_listings", lang === "ar" ? "المميّزة" : "Featured"],
              ["max_premium_listings", lang === "ar" ? "البريميوم" : "Premium"],
              ["max_employees", lang === "ar" ? "الموظفون" : "Employees"],
              ["max_branches", lang === "ar" ? "الفروع" : "Branches"],
              ["storage_gb", lang === "ar" ? "التخزين (GB)" : "Storage (GB)"],
              ["advertising_credits", lang === "ar" ? "أرصدة الإعلانات" : "Ad credits"],
              ["max_images_per_listing", lang === "ar" ? "صور/إعلان" : "Images/listing"],
              ["max_videos_per_listing", lang === "ar" ? "فيديوهات/إعلان" : "Videos/listing"],
            ].map(([k, label]) => (
              <label key={k} className="text-xs">
                <span className="block text-[#525252] mb-1">{label}</span>
                <input className="input-dark w-full" type="number" value={form.limits?.[k] ?? ""} onChange={(e) => setLimit(k, e.target.value)} data-testid={`plan-limit-${k}`} />
              </label>
            ))}
          </fieldset>

          <fieldset className="grid sm:grid-cols-2 gap-2">
            <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">
              {lang === "ar" ? "المميزات" : "Features"}
            </legend>
            {["analytics", "advanced_dashboard", "business_profile_customization", "priority_support", "api_access"].map((k) => (
              <label key={k} className="flex items-center gap-2 text-sm p-2 rounded border border-[#0a0a0a]/[0.06] cursor-pointer">
                <input type="checkbox" checked={!!form.features?.[k]} onChange={(e) => setFeat(k, e.target.checked)} className="w-4 h-4 accent-[#c9a55c]" data-testid={`plan-feature-${k}`} />
                {featureLabel(k, lang)}
              </label>
            ))}
          </fieldset>

          <fieldset className="grid sm:grid-cols-3 gap-3">
            <legend className="text-xs text-[#8a6f2e] uppercase tracking-widest mb-2 col-span-full">
              {lang === "ar" ? "الرؤية والترتيب" : "Visibility & order"}
            </legend>
            <label className="flex items-center gap-2 text-sm p-2 rounded border border-[#0a0a0a]/[0.06] cursor-pointer">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#c9a55c]" data-testid="plan-active" />
              {lang === "ar" ? "نشطة" : "Active"}
            </label>
            <label className="flex items-center gap-2 text-sm p-2 rounded border border-[#0a0a0a]/[0.06] cursor-pointer">
              <input type="checkbox" checked={!!form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} className="w-4 h-4 accent-[#c9a55c]" data-testid="plan-public" />
              {lang === "ar" ? "ظاهرة للعملاء" : "Public"}
            </label>
            <label className="text-xs">
              <span className="block text-[#525252] mb-1">{lang === "ar" ? "الترتيب" : "Sort"}</span>
              <input className="input-dark w-full" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} data-testid="plan-sort" />
            </label>
          </fieldset>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#0a0a0a]/[0.08]">
            <button type="button" onClick={onClose} className="btn-ghost">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="plan-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {lang === "ar" ? "حفظ" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
